import React, {
 forwardRef, useImperativeHandle, useRef, useState
} from 'react';
import {
  FormTemplate,
  FormTemplateSection,
} from 'data/template/template.types';
import * as types from 'data/tlsProfile/tlsProfile.types';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import './dialed-digit-extraction-server-form.scss';
import { FormLayout } from 'components/FormLayout';
import SectionLabel from 'components/SectionLabel';
import CommonGrid from 'shared/commonGrid';
import { CertStatusGridData } from 'data/certStatus/certStatus.types';
import i18n from 'i18n';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';
import { SelectionChangedEvent } from '@ag-grid-community/core';
import CertStatusActionMenus from '../CertStatusActionMenus/CertStatusActionMenus';

type Props = {
  template: FormTemplate;
  data: Partial<types.TlsProfile>;
  handleCertStatusSelectionChanged?: (event: SelectionChangedEvent) => void;
  i18nKeyPrefix?: string;
  isEditing?: boolean;
  onFieldUpdate?: (
    param: FieldUpdateHandlerParam,
    formSection?: 'main'
  ) => void;
  certGridData?: CertStatusGridData[];
  onCertActivate?: () => void;
  onCertDelete?: () => void;
  onRequestCert?: () => void;
  handleRefreshCertStatuses?: () => void;
  selectedCertStatus?: CertStatusGridData;
};

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean;
  getValues: () => types.TlsProfile;
};

export const COLLECTION_FUNCTION_GROUP_I18N_KEY =
  'tlsProfile.form';

export const TLSProfileServerForm = forwardRef<Ref, Props>(
  (
    {
      template,
      data,
      i18nKeyPrefix = COLLECTION_FUNCTION_GROUP_I18N_KEY,
      onFieldUpdate = () => null,
      certGridData,
      handleRefreshCertStatuses,
      onCertActivate,
      onCertDelete,
      onRequestCert,
      handleCertStatusSelectionChanged,
      selectedCertStatus
    }: Props,
    ref,
  ) => {
    const mainRef = useRef<Form>(null);

    const customFieldComponentMap = {
      privateKey: SectionLabel,
      certificate: SectionLabel,
      xccaCertParam: SectionLabel
    };

    const [columnDefs] = useState([
      {
        headerName: i18n.t('certStatus.grid.id'),
        field: 'id',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 97
      },
      {
        headerName: i18n.t('certStatus.grid.commonName'),
        field: 'commonName',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 97
      },
      {
        headerName: i18n.t('certStatus.grid.expireDate'),
        field: 'expireDate',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 97
      },
      {
        headerName: i18n.t('certStatus.grid.status'),
        field: 'status',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 97
      },
      {
        headerName: i18n.t('certStatus.grid.reason'),
        field: 'errorMsg',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 120
      },
    ]);

    const isValid = (forceValidation = false, forceTouched = true) => {
      const isMainValid =
        mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
      return isMainValid;
    };

    const getValues = () => {
      const formValues: types.TlsProfile = {
        ...mainRef.current?.getValues(),
      };
      return formValues;
    };

    useImperativeHandle(ref, () => ({
      isValid,
      getValues,
    }));

    const onMainFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'main');

    return (
      <div
        className="dialed-digit-extraction-server-form"
        data-testid="DialedDigitExtractionServerForm"
      >
        <div data-testid="DialedDigitExtractionServerFormMain">
          <FormLayout>
            <Form
              ref={mainRef}
              name={template.formId ?? template.key}
              defaults={data}
              fields={(template.main as FormTemplateSection).fields}
              layout={
                (template.main as FormTemplateSection).metadata.layout?.rows
              }
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={onMainFieldUpdate}
              customFieldComponentMap={customFieldComponentMap}
            />

            { certGridData && certGridData.length > 0 && (
              <div
                style={{ height: '560px', width: '80%' }}
                data-testid="CertStatus"
              >
                <CommonGrid
                  rowData={certGridData}
                  noResults={false}
                  columnDefs={columnDefs}
                  onSelectionChanged={handleCertStatusSelectionChanged}
                  hideSearchBar
                  rowSelection={ROWSELECTION.SINGLE}
                  onRefresh={handleRefreshCertStatuses}
                >
                  <h3>Cert Status</h3>

                  <CertStatusActionMenus
                    name={selectedCertStatus?.commonName || ''}
                    certStatus={selectedCertStatus}
                    allCertStatus={certGridData}
                    onActivate={onCertActivate}
                    onRequestCert={onRequestCert}
                    isEditing
                  />

                </CommonGrid>
              </div>
            )}
          </FormLayout>
        </div>
      </div>
    );
  },
);

export default TLSProfileServerForm;
